import React, { useEffect, useState } from "react";
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardTitle,
	Form,
	Row,
} from "reactstrap";

import digitzsPaymentValidation from "./DigitzsPaymentValidation";
import history from "../../utils/createBrowserHistory";
import formNavigationUtils from "../../utils/formNavigationUtils";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import ChildrenModal from "../../components/modals/ChildrenModal";
import DigitzsPaymentsTable from "./DigitzsPaymentsTable";
import FormattedMaskInput from "../../components/formattedMaskInput/FormattedMaskInput";
import InputElement from "../../features/forms/components/InputElement";
import ZipCityTable from "../../components/tables/ZipCityTable";

import {
	failAlert,
	successAlertCustom,
	twoOptionsAlert,
} from "../../utils/alertUtils";
import {
	getDigitzsPayments,
	processDigitzsTransaction,
} from "../../api/PaymentsAPI";
import {
	useDigitzsMerchantId,
	useFromUser,
} from "../../stores/LocalStorageHelper";
import {
	stateOptions,
	CountryCodes,
	CurrencyCodes,
	PATHS,
	digitzsCardTypes,
} from "../../constants/Constants";
import {
	CurrencyInputElement,
	IntegerInputElement,
	SelectorElement,
	ZipInputElement,
} from "../../features/forms/components/InputElement";
import { TIME_FORMAT } from "../../features/notes/NoteConstants";

import dayjs from "dayjs";

import { yupResolver } from "@hookform/resolvers";
import { FormProvider, useForm } from "react-hook-form";
import useSetAccount from "../../account/useSetAccount";
import useSetDeal from "../../features/deal/hooks/useSetDeal";

const DigitzsPayment = ({
	pmtAmount,
	// relation to other tables
	accountID = null,
	dealID = null,
	pickupPaymentID = null,
	// amount should add up to these paid values excluding convenience fee
	loanPaid = null,
	lateFeePaid = null,
	adjustmentPaid = null,
	pickupPaid = null,
	convenienceFee = null,
}) => {
	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");

	const settingsPage = PATHS.SETTINGS;
	const merchantId = useDigitzsMerchantId();

	const [cityModal, setCityModal] = useState(false);
	const [addressData, setAddressData] = useState({});
	const [showPaymentProgress, setShowPaymentProgress] = useState(false);
	const [payments, setPayments] = useState([]);
	const [miscData, setMiscData] = useState("");
	const [cardType, setCardType] = useState("");
	const [cardHolder, setCardHolder] = useState("");
	const [cardNumber, setCardNumber] = useState("");
	const [cardExpires, setCardExpires] = useState("");
	const [cardCode, setCardCode] = useState("");
	const [amount, setAmount] = useState(pmtAmount || 0);
	const [billingStreet, setBillingStreet] = useState("");
	const [billingUnit, setBillingUnit] = useState("");
	const [billingCity, setBillingCity] = useState("");
	const [billingState, setBillingState] = useState("");
	const [billingZip, setBillingZip] = useState("");
	const [billingCountry, setBillingCountry] = useState("USA");

	const setAccount = useSetAccount();
	const setDeal = useSetDeal();

	// Toggles the modal
	const toggleCityModal = () => setCityModal(!cityModal);

	const handleZipChange = (zip) => {
		setAddressData({
			zip,
			address: {
				city: billingCity,
				state: billingState,
			},
		});
		toggleCityModal();
	};

	const setAddressFields = (address) => {
		setBillingCity(address.city);
		setBillingState(address.state);
	};

	// Formats the date we display so that we can sort it
	const formatTransactionDate = (arr) => {
		return arr.map((obj) => {
			return {
				...obj,
				dmsTimestamp: dayjs(obj.dmsTimestamp).format(TIME_FORMAT),
			};
		});
	};

	// Asks if user wants to sign up for Digitzs
	const askToSignUp = async () => {
		const didConfirm = await twoOptionsAlert(
			"No Digitzs account found. Do you want to sign up for Digitzs?",
			"Yes",
			"No"
		);

		if (didConfirm === "Yes") {
			history.push({
				pathname: settingsPage,
				state: {
					tab: "signUp",
				},
			});
		}
	};

	// Checks that amount is at least 1, per digitzs requirement
	const isAmountValid = () => {
		if (amount < 1) {
			methods.setError("digitzsAmount", {
				message: "Must be at least 1",
				shouldFocus: true,
			});

			return false;
		}

		return true;
	};

	// Calls api to process payment if all data is valid
	const processPayment = () => {
		if (!isAmountValid()) {
			return;
		}

		if (merchantId !== null) {
			setShowPaymentProgress(true);

			const paymentData = {
				dealerID,
				locationID,

				accountID,
				dealID,
				pickupPaymentID,
				// amount should add up to these paid values excluding convenience fee
				loanPaid,
				lateFeePaid,
				adjustmentPaid,
				pickupPaid,
				convenienceFee,

				paymentRequest: {
					miscData:
						miscData == null || miscData.trim().length === 0 ? null : miscData,
					paymentType: "card",
					merchantId,
					card: {
						type: cardType,
						holder: cardHolder,
						number: cardNumber,
						expiry: cardExpires,
						code: cardCode,
						label: cardType + "-" + cardNumber.slice(-4),
					},
					transaction: {
						amount,
						currency: "USD",
						invoice: null,
					},
					billingAddress: {
						line1: billingStreet,
						line2: billingUnit.trim().length === 0 ? null : billingUnit,
						city: billingCity,
						state: billingState,
						zip: billingZip,
						country: billingCountry,
					},
				},
			};

			processDigitzsTransaction(paymentData).then(
				(res) => {
					// this can return either a deal or account object.
					if (paymentData.accountID !== null) {
						// set account obj
						setAccount(res);
					} else if (paymentData.dealID !== null) {
						// set deal obj
						setDeal(res.data.content);
					}
					setShowPaymentProgress(false);
					successAlertCustom(res.data.message);
				},
				(err) => {
					setShowPaymentProgress(false);
					failAlert(
						err?.response?.data?.message || "Failed to process payment"
					);
				}
			);
		} else {
			askToSignUp();
		}
	};

	// Shows the customer's payment history table
	// useEffect(() => {
	// 	if (customerID != null) {
	// 		getDigitzsPayments(customerID).then(
	// 			(res) => setPayments(formatTransactionDate(res.data.content.payments)),
	// 			(err) => console.log(err)
	// 		);
	// 	}
	// 	// eslint-disable-next-line
	// }, []);

	// Define form validation parameters
	const methods = useForm({
		reValidateMode: "onBlur",
		resolver: yupResolver(digitzsPaymentValidation),
		shouldFocusError: true,
	});

	return (
		<div className="col-lg-12 mx-auto">
			<FormProvider {...methods}>
				<Form onKeyDown={formNavigationUtils}>
					<Card className="pt-2">
						<CardHeader>
							<CardTitle>
								<h3>Payment Terminal</h3>
							</CardTitle>
						</CardHeader>
						{(showPaymentProgress && <LoadingSpinner />) || (
							<div>
								<CardBody className="mx-1">
									<Row>
										<CurrencyInputElement
											{...methods}
											value={amount}
											onChange={setAmount}
											name="digitzsAmount"
											placeholder="Enter amount to charge"
											label="Amount"
										/>
										<SelectorElement
											{...methods}
											colSize="col-lg-4"
											name="digitzsCardType"
											label="Card Type"
											value={cardType}
											onChange={(e) => setCardType(e.target.value)}
											options={digitzsCardTypes}
										/>
									</Row>
									<Row>
										<InputElement
											{...methods}
											colSize="col-lg-3"
											value={cardHolder}
											onChange={(e) => setCardHolder(e.target.value)}
											name="digitzsCardHolder"
											label="Name"
											placeholder=""
											type="text"
										/>
										<FormattedMaskInput
											{...methods}
											colSize="col-lg-4"
											format="#### #### #### ####"
											value={cardNumber}
											onChange={setCardNumber}
											name="digitzsCardNumber"
											label="Card Number"
										/>
										<FormattedMaskInput
											{...methods}
											colSize="col-md-2"
											format="##/##"
											placeholder="MM/YY"
											mask={["M", "M", "Y", "Y"]}
											value={cardExpires}
											onChange={setCardExpires}
											name="digitzsCardExpires"
											label="Exp."
										/>
										<FormattedMaskInput
											{...methods}
											colSize="col-lg-2"
											format="###"
											value={cardCode}
											onChange={setCardCode}
											name="digitzsCardCode"
											label="CVV"
											placeholder="Security Code"
										/>
									</Row>
								</CardBody>
								<CardBody className="row mx-1 mt-3">
									<CardTitle tag="h3" className="col-12 mb-3">
										Billing Address
									</CardTitle>
									<InputElement
										{...methods}
										colSize="col-lg-3"
										value={billingStreet}
										onChange={(e) => setBillingStreet(e.target.value)}
										name="digitzsBillingStreet"
										label="Street"
										type="text"
									/>
									<InputElement
										{...methods}
										colSize="col-lg-2"
										value={billingUnit}
										onChange={(e) => setBillingUnit(e.target.value)}
										name="digitzsBillingUnit"
										label="Apt/Unit/Bld"
										type="text"
									/>
									<InputElement
										{...methods}
										value={billingCity}
										onChange={(e) => setBillingCity(e.target.value)}
										name="digitzsBillingCity"
										label="City"
										type="text"
									/>
									<SelectorElement
										{...methods}
										colSize="col-2"
										name="digitzsBillingState"
										label="State"
										value={billingState}
										onChange={(e) => setBillingState(e.target.value)}
										options={stateOptions}
									/>
									<ZipInputElement
										{...methods}
										colSize="col-lg-2"
										value={billingZip}
										onChange={setBillingZip}
										openZipModal={handleZipChange}
										name="digitzsBillingZip"
										label="Zip"
									/>
									<SelectorElement
										{...methods}
										colSize="col-lg-3"
										name="digitzsBillingCountry"
										label="Country"
										value={billingCountry}
										onChange={(e) => setBillingCountry(e.target.value)}
										options={CountryCodes}
									/>
									<InputElement
										{...methods}
										colSize="col-lg-12"
										value={miscData}
										name="digitzsNotes"
										label="Additional Info"
										placeholder="Enter any additional information"
										type="textarea"
										onChange={(e) => setMiscData(e.target.value)}
									/>
									<div className="col-12 d-flex justify-content-center my-3">
										<Button
											color="primary"
											onClick={methods.handleSubmit(processPayment)}
										>
											Process Payment
										</Button>
									</div>
									<ChildrenModal
										modal={cityModal}
										toggle={toggleCityModal}
										modalTitle="Cities"
									>
										<ZipCityTable
											data={addressData}
											toggle={toggleCityModal}
											address={{
												zip: billingZip,
												city: billingCity,
												state: billingState,
											}}
											setAddress={setAddressFields}
										/>
									</ChildrenModal>
								</CardBody>
							</div>
						)}
					</Card>
					{payments.length > 0 && (
						<Card className="mx-1 mt-3">
							<CardTitle
								tag="h3"
								className="d-flex justify-content-center col-12 my-3 mx-3"
							>
								Customer's Payments
							</CardTitle>
							<CardBody className="row-12 mx-1">
								<DigitzsPaymentsTable
									payments={payments}
									editPayments={setPayments}
								/>
							</CardBody>
						</Card>
					)}
				</Form>
			</FormProvider>
		</div>
	);
};

export default DigitzsPayment;
